import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { toast } from "sonner";
import { HardHat, Plus, CaretDown, WarningCircle } from "@phosphor-icons/react";

const STATUS_DOT = { draft: "bg-slate-400", issued: "bg-emerald-500", superseded: "bg-red-400" };

/** Header action for job / emergency job / job request pages: create a RAMS from the entity, or open one already linked to it.
 *  `warn` = the job is scheduled/crewed with no RAMS → the empty state turns red ("No RAMS — create"). */
export default function CreateRamsButton({ entityType, entityId, size = "sm", className = "", testId = "create-rams-btn", warn = false }) {
  const navigate = useNavigate();
  const [rams, setRams] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!entityId) return;
    API.get("/hs/rams", { params: { entity_type: entityType, entity_id: entityId } })
      .then(({ data }) => setRams((data.rams || []).filter(r => r.status !== "superseded"))).catch(() => {});
  }, [entityType, entityId]);
  useEffect(() => { load(); }, [load]);
  // The auto RAMS lands a few seconds after a record is created — poll briefly while nothing is linked yet
  useEffect(() => {
    if (rams.length > 0) return undefined;
    let n = 0;
    const t = setInterval(() => { n += 1; if (n > 10) clearInterval(t); else load(); }, 8000);
    return () => clearInterval(t);
  }, [rams.length, load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/hs/rams", { entity_type: entityType, entity_id: entityId });
      toast.success(`${data.reference} created — pulled job details, crew and nearest A&E`);
      navigate(`/health-safety/rams/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (rams.length === 0) {
    const red = warn ? "border-red-400 text-red-700 bg-red-50 hover:bg-red-100 dark:bg-red-950/30 dark:text-red-200" : "";
    return (
      <Button variant="outline" size={size} onClick={create} disabled={busy} className={`${red} ${className}`} data-testid={testId} data-rams-warn={warn ? "1" : undefined}>
        {warn ? <WarningCircle size={14} weight="fill" className="mr-1" /> : <HardHat size={14} className="mr-1" />} {busy ? "Creating…" : warn ? "No RAMS — create" : "Create RAMS"}
      </Button>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size={size} className={className} data-testid={testId}>
          <HardHat size={14} className="mr-1" /> RAMS ({rams.length}) <CaretDown size={12} className="ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72" data-testid={`${testId}-menu`}>
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Linked RAMS</DropdownMenuLabel>
        {rams.map(r => (
          <DropdownMenuItem key={r.id} onClick={() => navigate(`/health-safety/rams/${r.id}`)} className="text-xs" data-testid={`${testId}-open-${r.id}`}>
            <span className={`inline-block h-2 w-2 rounded-full mr-2 ${STATUS_DOT[r.status] || "bg-slate-400"}`} />
            <span className="font-mono mr-2">{r.reference}</span>
            <span className="truncate flex-1">{r.title}</span>
            <span className="ml-2 text-[10px] text-muted-foreground">v{r.version} · {r.status}</span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={create} disabled={busy} className="text-xs font-medium" data-testid={`${testId}-new`}>
          <Plus size={14} className="mr-2" /> Create new RAMS
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
